/**
 * Display wording that depends on the home's state.
 * Stored keys (region_code, state, hurricane_shutters) stay as they are.
 */

const { stripTaskEmoji } = require('./taskEmoji');

const STATE_NAMES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VI: 'U.S. Virgin Islands',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

/** Atlantic and Gulf coasts, Hawaii, and the Caribbean territories. */
const HURRICANE_STATE_CODES = new Set([
  'AL', 'CT', 'DE', 'FL', 'GA', 'HI', 'LA', 'MA', 'MD', 'ME',
  'MS', 'NC', 'NH', 'NJ', 'NY', 'PR', 'RI', 'SC', 'TX', 'VA', 'VI',
]);

const NAMES_TO_CODES = Object.keys(STATE_NAMES).reduce((map, code) => {
  map[STATE_NAMES[code].toUpperCase()] = code;
  return map;
}, {});

function knownCode(value) {
  const code = String(value || '').trim().toUpperCase();
  if (!code) return '';
  if (STATE_NAMES[code]) return code;
  return NAMES_TO_CODES[code] || '';
}

/**
 * region_code wins when it names a state. Otherwise use state.
 * An unknown home returns ''.
 */
function stateCode(home) {
  if (!home) return '';
  const region = String(home.region_code || '').trim().toUpperCase();
  if (region) {
    const fromRegion = knownCode(region.replace(/^US-/, ''));
    if (fromRegion) return fromRegion;
  }
  return knownCode(home.state);
}

function isFloridaHome(home) {
  return stateCode(home) === 'FL';
}

function isHurricaneHome(home) {
  return HURRICANE_STATE_CODES.has(stateCode(home));
}

/**
 * Hurricane season labels stay for Florida and other hurricane states.
 * Everywhere else, a hurricane season line reads "Weather events".
 */
function displaySeason(season, home) {
  const text = String(season == null ? '' : season).trim();
  if (!text) return '';
  if (isHurricaneHome(home)) return text;
  if (/hurricane/i.test(text)) return 'Weather events';
  return text;
}

function displayTaskDescription(description, home) {
  const text = stripTaskEmoji(description);
  if (!text) return '';
  if (isHurricaneHome(home)) return text;
  return text.replace(/Season:\s*([^\n]*)/g, (match, season) => (
    'Season: ' + displaySeason(season, home)
  ));
}

module.exports = {
  STATE_NAMES,
  HURRICANE_STATE_CODES,
  stateCode,
  isFloridaHome,
  isHurricaneHome,
  displaySeason,
  displayTaskDescription,
};
